// Flawked: show the selected colour's photo in the product gallery.
// The theme re-renders the gallery on every colour change but opens it on photo 1,
// so we find the selected variant's photo (by media id) and move the gallery to it.

const fileName = (u) => (u || '').split('?')[0].split('/').pop();
let wanted = null; // { id, file } of the selected variant's photo

function mediaOf(variant) {
  const m = variant?.featured_media;
  if (!m && !variant?.featured_image) return null;
  return { id: String(m?.id || ''), file: fileName(m?.preview_image?.src || variant?.featured_image?.src) };
}

function variantFromPage(root = document) {
  const script = root.querySelector('variant-picker script[type="application/json"]');
  try {
    return JSON.parse(script?.textContent || 'null');
  } catch {
    return null;
  }
}

function slideIndex(el, media) {
  const slides = el.slides || Array.from(el.querySelectorAll('slideshow-slide'));
  let i = media.id ? slides.findIndex((s) => s.querySelector(`[data-media-id="${media.id}"]`)) : -1;
  if (i < 0 && media.file) {
    i = slides.findIndex((s) =>
      Array.from(s.querySelectorAll('img')).some(
        (img) => fileName(img.getAttribute('src')) === media.file || (img.getAttribute('srcset') || '').includes('/' + media.file)
      )
    );
  }
  return i;
}

function scrollerOf(el) {
  return el.refs?.scroller || el.querySelector('slideshow-slides');
}

// True when slide i is actually the one sitting in view, not just marked as current.
function isShown(el, i) {
  const sc = scrollerOf(el);
  const slide = (el.slides || [])[i];
  if (!sc || !slide || !sc.clientWidth) return false;
  return Math.abs(slide.getBoundingClientRect().left - sc.getBoundingClientRect().left) < 2;
}

function goTo(el, i) {
  if (!isShown(el, i)) {
    if (el.current === i) el.current = i === 0 ? 1 : 0;
    el.select(i, undefined, { animate: false });
  }
  // The theme can mark the slide as current (thumbnail highlighted) without scrolling,
  // so scroll the photo strip ourselves using where the slide really is on screen.
  const sc = scrollerOf(el);
  const slide = (el.slides || [])[i];
  if (sc && slide && sc.clientWidth && !isShown(el, i)) {
    const delta = slide.getBoundingClientRect().left - sc.getBoundingClientRect().left;
    sc.scrollTo({ left: sc.scrollLeft + delta, behavior: 'instant' });
    el.current = i;
  }
  return isShown(el, i);
}

let token = 0;
const hooked = new WeakSet();
function sync() {
  if (!wanted) return;
  const t = ++token;
  let tries = 0;
  let touched = false;
  const stop = () => (touched = true);
  const tick = () => {
    if (t !== token || touched) return;
    const el = document.querySelector('media-gallery slideshow-component');
    if (el) {
      const gallery = el.closest('media-gallery') || el;
      if (!hooked.has(gallery)) {
        hooked.add(gallery);
        for (const ev of ['pointerdown', 'touchstart', 'wheel', 'keydown']) gallery.addEventListener(ev, stop, { passive: true });
      }
      const i = slideIndex(el, wanted);
      // The theme can re-render or snap the gallery back late, so keep the
      // chosen colour's photo in view for a few seconds unless the shopper takes over.
      if (i >= 0 && !isShown(el, i)) goTo(el, i);
    }
    if (tries++ < 50) setTimeout(tick, 120);
  };
  tick();
}

// A shopper picked a colour: the theme's own event carries the new variant.
import('@shopify/events')
  .then(({ StandardEvents }) => {
    document.addEventListener(
      StandardEvents.productSelect,
      (event) => {
        if (event.target instanceof Element && event.target.closest('product-card')) return;
        event.promise
          ?.then(({ detail }) => {
            const media = mediaOf(detail?.resource) || mediaOf(detail?.html && variantFromPage(detail.html));
            if (media) {
              wanted = media;
              sync();
            }
          })
          .catch(() => {});
      },
      true
    );
  })
  .catch(() => {});

// The gallery is replaced after the event resolves; move it again once it is in the page.
new MutationObserver((records) => {
  for (const r of records) {
    for (const n of r.addedNodes) {
      if (n instanceof Element && (n.matches('media-gallery') || n.querySelector('media-gallery'))) {
        wanted = wanted || mediaOf(variantFromPage());
        return sync();
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });

// Deep link to a colour (?variant=...): open on that colour's photo.
const params = new URL(location.href).searchParams;
if (params.has('variant') || params.has('option_values')) {
  wanted = mediaOf(variantFromPage());
  sync();
}
